import React, { useRef, lazy, Suspense, useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts, api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { GlassArea, GlassPanel, TrustPoints } from "@/components/HomeGlass";
import HomeHeroCarousel from "@/components/HomeHeroCarousel";
const RitualBuilder = lazy(() => import("@/components/RitualBuilder"));
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const CAROUSEL_CATS = [
  { label: "Face Care", to: "/shop/skincare", countKey: "skincare", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/88cabdd6c4f110e1_Joyful%20Freckled%20Beauty%20Portrait1.png", color: "#E8D5C4" },
  { label: "Body Care", to: "/shop/body", countKey: "body", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/04gkhlyc_bodycare6.webp", color: "#C9DDE8" },
  { label: "Hair Care", to: "/shop/hair", countKey: "hair", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/ewojrlrf_haircare.webp", color: "#D4C4A8" },
  { label: "Makeup", to: "/shop/makeup", countKey: "makeup", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/brn8yb3o_makeup8.webp", color: "#E0C8D0" },
  { label: "Fragrance", to: "/shop/fragrance", countKey: "fragrance", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/6leyctzw_fraguance1.webp", color: "#D8D0E0" },
  { label: "Hot Deals", to: "/shop/hot-deals", countKey: "hot-deals", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/li4gg22a_pexels-karola-g-5625048.jpg", color: "#E0C0C0" },
  { label: "New Arrivals", to: "/shop/new-arrivals", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/655l0ti6_body1.webp", color: "#C4D8C8" },
];

function CategoryCard({ cat, count }) {
  const slug = cat.label.toLowerCase().replace(/\s+/g, "-");
  const countText = count == null ? null : count > 0 ? `${count} ${count === 1 ? "product" : "products"}` : cat.countKey === "hot-deals" ? "Coming soon" : null;
  return (
    <Link
      to={cat.to}
      draggable={false}
      className="snap-start flex-shrink-0 w-[64%] sm:w-[calc((100%-2*16px)/2.4)] md:w-[calc((100%-2*20px)/3)] lg:w-[calc((100%-3*20px)/4)] group select-none rounded-[20px] bg-white/70 hover:bg-white p-2.5 sm:p-3 transition-colors duration-300"
      data-testid={`carousel-${slug}`}
    >
      <div className="aspect-[16/10] rounded-[14px] overflow-hidden bg-[#F2EFEC]">
        <img src={cat.image} alt={cat.label} draggable={false} className="w-full h-full object-cover object-[50%_30%] group-hover:scale-105 transition-transform duration-700" loading="lazy" />
      </div>
      <div className="pt-3.5 pb-2 text-center">
        <p className="font-sans text-base md:text-[17px] font-medium text-[#111111]">{cat.label}</p>
        {countText && (
          <p className="font-sans text-xs text-[#333333]/55 mt-0.5" data-testid={`carousel-count-${slug}`}>{countText}</p>
        )}
      </div>
    </Link>
  );
}

const ARROW_BTN = "w-11 h-11 flex items-center justify-center rounded-full bg-white/80 hover:bg-[#111111] hover:text-white transition-colors duration-200 text-[#333333]";

function CategoryCarousel() {
  const scrollRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [overflows, setOverflows] = useState(false);
  const { data: counts = {} } = useQuery({
    queryKey: ["category-counts"],
    queryFn: async () => (await api.get("/products/category-counts")).data,
    staleTime: 10 * 60 * 1000,
  });

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = scrollRef.current;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
  };

  useEffect(() => {
    const el = scrollRef.current;
    const check = () => setOverflows(el.scrollWidth - el.clientWidth > 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [counts]);

  return (
    <GlassPanel testId="category-carousel-section" className="py-10 sm:py-14 lg:py-16">
      <h2 className="font-sans font-bold text-[30px] sm:text-4xl lg:text-[44px] tracking-[-0.03em] leading-tight text-[#111111] text-center mb-7 md:mb-10 px-5">Shop by Category</h2>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        data-testid="category-carousel-track"
        className="flex gap-4 md:gap-5 overflow-x-auto no-scrollbar pb-2 snap-x snap-mandatory overscroll-x-contain px-5 sm:px-10 lg:px-14 scroll-px-5 sm:scroll-px-10 lg:scroll-px-14"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {CAROUSEL_CATS.map((cat) => (cat.countKey === "hot-deals" && !counts["hot-deals"]) ? null : (
          <CategoryCard key={cat.label} cat={cat} count={cat.countKey ? counts[cat.countKey] : null} />
        ))}
      </div>

      <div className={`flex items-center justify-center gap-4 mt-6 px-5 ${overflows ? "" : "invisible"}`}>
        <button onClick={() => scroll("left")} aria-label="Scroll left" data-testid="carousel-prev" className={`hidden md:flex ${ARROW_BTN}`}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="h-[3px] w-40 sm:w-56 bg-[#333333]/10 rounded-full overflow-hidden" data-testid="carousel-progress">
          <div className="h-full bg-[#111111] rounded-full transition-[width] duration-150" style={{ width: `${Math.max(18, progress * 100)}%` }} />
        </div>
        <button onClick={() => scroll("right")} aria-label="Scroll right" data-testid="carousel-next" className={`hidden md:flex ${ARROW_BTN}`}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </GlassPanel>
  );
}

const GOALS = ["GLOW", "HYDRATE", "CLEAR", "ANTI-AGING", "REPAIR", "RELAX"];

const SCROLL_CATEGORIES = [
  { label: "Face Care", to: "/shop/skincare", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/pseilzwp_face2.webp" },
  { label: "Body Care", to: "/shop/body", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/04gkhlyc_bodycare6.webp" },
  { label: "Hair Care", to: "/shop/hair", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/ewojrlrf_haircare.webp" },
  { label: "Makeup", to: "/shop/makeup", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/brn8yb3o_makeup8.webp" },
  { label: "Fragrance", to: "/shop/fragrance", image: "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/6leyctzw_fraguance1.webp" },
];

function ShopByCategory() {
  const [activeIdx, setActiveIdx] = useState(0);
  const refs = useRef([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.idx);
            if (!isNaN(idx)) setActiveIdx(idx);
          }
        });
      },
      { threshold: 0.6 }
    );
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#FBF7F3] py-20 lg:py-28 border-t border-[#333333]/05" data-testid="shop-by-category-scroll" ref={sectionRef}>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        {/* Desktop: side by side */}
        <div className="hidden lg:grid grid-cols-2 gap-20 min-h-[600px]">
          <div className="flex flex-col justify-center gap-4">
            {SCROLL_CATEGORIES.map((cat, i) => (
              <Link
                key={cat.label}
                to={cat.to}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`scroll-cat-${cat.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-center justify-between py-4 border-b border-[#333333]/05 transition-all duration-300"
              >
                <span className={`font-serif text-4xl tracking-tight transition-colors duration-300 ${activeIdx === i ? "text-[#333333] font-bold" : "text-[#333333]/15"}`}>
                  {cat.label}
                </span>
                <ArrowRight className={`w-5 h-5 transition-all duration-300 ${activeIdx === i ? "text-[#333333] translate-x-0 opacity-100" : "text-[#333333]/10 -translate-x-2 opacity-0"}`} />
              </Link>
            ))}
          </div>
          <div className="relative rounded-sm overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.img
                key={activeIdx}
                src={SCROLL_CATEGORIES[activeIdx].image}
                alt={SCROLL_CATEGORIES[activeIdx].label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile: stacked scroll-driven cards */}
        <div className="lg:hidden space-y-6">
          <h2 className="font-serif text-3xl text-[#333333] tracking-tight text-center mb-8">Shop by Category</h2>
          {SCROLL_CATEGORIES.map((cat, i) => (
            <div key={cat.label} ref={(el) => (refs.current[i] = el)} data-idx={i}>
              <Link to={cat.to} className="block group">
                <div className="aspect-[16/9] rounded-sm overflow-hidden mb-3">
                  <img src={cat.image} alt={cat.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <p className={`font-serif text-xl transition-colors duration-300 ${activeIdx === i ? "text-[#333333] font-bold" : "text-[#333333]/30"}`}>{cat.label}</p>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---- Hero Slider — 100vh VETRO-style ---- */
const HOME_VIDEO_MP4 = "https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/66db05271d279722_og3.mp4";
const HOME_VIDEO_WEBM = "/media/og3.webm";
const HOME_VIDEO_POSTER = "/media/og3-poster.jpg";

function HomeVideo() {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    const el = ref.current;
    el.muted = true;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setSrc(el.canPlayType('video/mp4; codecs="avc1.640028"') ? HOME_VIDEO_MP4 : HOME_VIDEO_WEBM);
        el.play().catch(() => {});
      } else {
        el.pause();
      }
    }, { rootMargin: "300px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (src) ref.current.play().catch(() => {});
  }, [src]);

  return (
    <section className="bg-[#FBF7F3] py-14 sm:py-20 lg:py-24" data-testid="home-video-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-7 lg:gap-16 items-center">
        <div className="rounded-[20px] sm:rounded-[28px] overflow-hidden bg-[#F4EEE8]">
          <video
            ref={ref}
            src={src || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={HOME_VIDEO_POSTER}
            width={2048}
            height={1080}
            style={{ aspectRatio: "2048 / 1080" }}
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback"
            aria-hidden="true"
            tabIndex={-1}
            className="block w-full h-auto object-cover pointer-events-none select-none"
            data-testid="home-video"
          />
        </div>
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#333333] tracking-tight leading-[1.1]" data-testid="home-video-headline">
            Cleanse. Glow. Repeat.
          </h2>
          <Link
            to="/shop/skincare"
            data-testid="home-video-shop-face-care"
            className="group inline-flex items-center gap-2 h-12 px-7 mt-6 sm:mt-8 rounded-full bg-[#111111] text-white text-[12px] tracking-[0.15em] uppercase font-sans font-medium hover:bg-[#333333] transition-colors duration-300"
          >
            Shop Face Care
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const PRODUCT_SLOT = "snap-start flex-shrink-0 w-[calc((100%-12px)/2.15)] sm:w-[calc((100%-2*16px)/2.6)] md:w-[calc((100%-2*16px)/3)] lg:w-[calc((100%-3*20px)/4)]";

const EX_TABS = [
  { key: "best-sellers", label: "Best sellers" },
  { key: "hot-deals", label: "Hot deals" },
  { key: "new-arrivals", label: "New arrivals" },
];

export default function HomePage() {
  /* ---- data ---- */
  const { data: bestSellersCollection = [], isLoading: bsLoading } = useQuery({
    queryKey: ["products", "bestsellers"],
    queryFn: () => fetchProducts({ collection: "bestsellers", limit: 12 }),
  });
  const { data: bestSellingAll = [], isLoading: bsAllLoading } = useQuery({
    queryKey: ["products", "best-selling-all"],
    queryFn: () => fetchProducts({ sort: "featured", limit: 12 }),
    enabled: bestSellersCollection.length === 0 && !bsLoading,
  });
  const bestSellers =
    bestSellersCollection.length > 0 ? bestSellersCollection : bestSellingAll;
  const bsStillLoading =
    bsLoading || (bestSellersCollection.length === 0 && bsAllLoading);

  /* ---- "Exclusively for you" tabs ---- */
  const { data: newArrivals = [] } = useQuery({
    queryKey: ["products", "new-arrivals-home"],
    queryFn: () => fetchProducts({ collection: "new-arrivals", limit: 10 }),
  });
  const { data: hotDealsRaw = [] } = useQuery({
    queryKey: ["products", "hot-deals"],
    queryFn: () => fetchProducts({ search: "tag:hot-deals", limit: 20 }),
  });
  const hotDeals = hotDealsRaw;
  const [exTab, setExTab] = useState("best-sellers");
  const exProducts = exTab === "best-sellers" ? bestSellers : exTab === "new-arrivals" ? newArrivals : hotDeals;
  const exRef = useRef(null);
  const [exEdge, setExEdge] = useState({ start: true, end: false });
  const updateExEdge = useCallback(() => {
    const el = exRef.current;
    if (el) setExEdge({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  }, []);
  useEffect(() => {
    const el = exRef.current;
    if (!el) return undefined;
    el.scrollTo({ left: 0 });
    updateExEdge();
    const ro = new ResizeObserver(updateExEdge);
    ro.observe(el);
    return () => ro.disconnect();
  }, [exTab, exProducts.length, updateExEdge]);

  /* ---- scroll helpers ---- */
  const trendingRef = useRef(null);
  const editRef = useRef(null);
  const scroll = (ref, dir) => {
    if (!ref.current) return;
    const amt = ref.current.offsetWidth * 0.7;
    ref.current.scrollBy({
      left: dir === "left" ? -amt : amt,
      behavior: "smooth",
    });
  };

  /* ---- skeleton helper ---- */
  const skeletons = (n) =>
    Array.from({ length: n }).map((_, i) => (
      <div key={i} className={`${PRODUCT_SLOT} animate-pulse rounded-lg bg-white overflow-hidden`}>
        <div className="aspect-square bg-[#333333]/5" />
        <div className="mx-4 sm:mx-5 mt-4 h-3 w-20 bg-[#333333]/5 rounded" />
        <div className="mx-4 sm:mx-5 mt-2 mb-6 h-4 w-3/4 bg-[#333333]/5 rounded" />
      </div>
    ));

  return (
    <div data-testid="home-page" className="bg-[#FBF7F3]">
      <HomeHeroCarousel />

      <GlassArea>
        {/* New Arrivals — tabbed products */}
        <GlassPanel testId="exclusively-section" className="py-8 sm:py-12 lg:py-14">
          <div className="mb-6 sm:mb-8 px-5 sm:px-10 lg:px-14">
            <h2 className="font-serif text-3xl md:text-4xl text-[#333333] tracking-tight">New Arrivals</h2>
            <div className="flex items-center justify-between gap-4 mt-5 sm:mt-6">
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0" role="tablist" data-testid="ex-tabs">
                {EX_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={exTab === tab.key}
                    onClick={() => setExTab(tab.key)}
                    data-testid={`ex-tab-${tab.key}`}
                    className={`flex-shrink-0 h-11 sm:h-12 px-5 sm:px-6 rounded-full border-[1.5px] border-[#111111] whitespace-nowrap text-[11px] sm:text-[12px] tracking-[0.12em] uppercase font-sans font-semibold transition-colors duration-200 ${
                      exTab === tab.key ? "bg-[#111111] text-white" : "bg-transparent text-[#111111] hover:bg-[#111111]/5"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                {["left", "right"].map((dir) => (
                  <button
                    key={dir}
                    onClick={() => scroll(exRef, dir)}
                    disabled={dir === "left" ? exEdge.start : exEdge.end}
                    aria-label={dir === "left" ? "Previous products" : "Next products"}
                    data-testid={`ex-${dir === "left" ? "prev" : "next"}`}
                    className="w-12 h-12 rounded-full border-[1.5px] border-[#111111] text-[#111111] flex items-center justify-center transition-colors duration-200 hover:bg-[#111111] hover:text-white disabled:border-[#111111]/20 disabled:text-[#111111]/25 disabled:hover:bg-transparent disabled:cursor-default"
                  >
                    {dir === "left" ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div
            ref={exRef}
            onScroll={updateExEdge}
            className="flex gap-3 sm:gap-4 lg:gap-5 overflow-x-auto no-scrollbar pb-2 snap-x snap-mandatory overscroll-x-contain px-5 sm:px-10 lg:px-14 scroll-px-5 sm:scroll-px-10 lg:scroll-px-14"
            data-testid="exclusively-products"
          >
            {(bsStillLoading && exTab === "best-sellers") ? skeletons(5) : (
              exProducts.slice(0, 8).map((p, i) => (
                <div key={p.id} className={PRODUCT_SLOT}>
                  <ProductCard
                    product={p}
                    index={i}
                    contain
                    isBestseller={exTab === "best-sellers" && i < 3}
                  />
                </div>
              ))
            )}
            {exProducts.length === 0 && !bsStillLoading && (
              <p className="font-sans text-sm text-[#333333]/50 italic py-8">Coming soon...</p>
            )}
          </div>
        </GlassPanel>

        <TrustPoints />

        <CategoryCarousel />

        <Suspense fallback={<div className="min-h-[600px]" />}>
          <RitualBuilder variant="home" />
        </Suspense>
      </GlassArea>

      <HomeVideo />

      {/* Newsletter */}
      <section className="bg-[#FBF7F3] px-3 sm:px-6 lg:px-12 pb-14 sm:pb-20" data-testid="newsletter-banner">
        <div className="max-w-[1600px] mx-auto rounded-[24px] sm:rounded-[32px] bg-[#F6ECE6] px-6 sm:px-12 lg:px-16 py-10 sm:py-14 flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#333333] tracking-tight">Stay in the Know</h2>
            <p className="font-sans text-[15px] text-[#333333]/70 mt-2 max-w-md">New arrivals, exclusive offers, and beauty tips — straight to your inbox.</p>
          </div>
          <form className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:max-w-md">
            <input
              placeholder="Enter your email"
              type="email"
              data-testid="newsletter-email-input"
              className="w-full sm:flex-1 sm:w-auto min-w-0 h-12 flex-shrink-0 bg-white rounded-full px-5 text-base font-sans text-[#333333] placeholder:text-[#333333]/45 focus:outline-none focus:ring-2 focus:ring-[#333333]/15 transition-shadow"
            />
            <button
              type="button"
              data-testid="newsletter-submit-btn"
              className="h-12 px-7 rounded-full bg-[#111111] text-[12px] tracking-[0.15em] uppercase font-sans font-medium text-white hover:bg-[#333333] transition-colors duration-300"
            >
              Join the List
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
